<template>
  <div class="income-expense-account-widget-container">
    <div v-if="loading" class="income-expense-account-widget-loading">Loading...</div>
    <div v-else-if="error" class="income-expense-account-widget-error">{{ error }}</div>
    <div v-else class="income-expense-account-widget-content">
      <!-- Original Summary View -->
      <div class="income-expense-account-widget-summary">
        <div class="income-expense-account-widget-summary-item income-expense-account-widget-income">
          <div class="income-expense-account-widget-summary-label">Total Income</div>
          <div class="income-expense-account-widget-summary-value income-expense-account-widget-positive">
            {{ formatCurrency(totalIncome) }}
          </div>
        </div>
        <div class="income-expense-account-widget-summary-item income-expense-account-widget-expense">
          <div class="income-expense-account-widget-summary-label">Total Expenses</div>
          <div class="income-expense-account-widget-summary-value income-expense-account-widget-negative">
            {{ formatCurrency(totalExpenses) }}
          </div>
        </div>
      </div>
      <div class="income-expense-account-widget-net">
        <div class="income-expense-account-widget-net-label">Net Income</div>
        <div class="income-expense-account-widget-net-value" :class="netIncomeClass">
          {{ formatCurrency(netIncome) }}
        </div>
      </div>
      <div class="income-expense-account-widget-bar">
        <div class="income-expense-account-widget-bar-income" :style="{ width: incomePercentage + '%' }"></div>
        <div class="income-expense-account-widget-bar-expense" :style="{ width: expensePercentage + '%' }"></div>
      </div>
      
      <!-- New Detailed View -->
      <div class="income-expense-account-widget-detail-list">
        <div class="income-expense-account-widget-detail-row">
          <div class="income-expense-account-widget-detail-label">
            <span class="income-expense-account-widget-label-text">TOTAL INCOME</span>
            <span 
              class="income-expense-account-widget-info-icon" 
              @click="showInfoModal('Total Income', 'Total Income = Income + Other Income', 'Total Income is the sum of all revenue from sales and other income sources.')"
              title="What is Total Income?"
            >
              <i class="bi bi-question-circle"></i>
            </span>
          </div>
          <div class="income-expense-account-widget-detail-value income-expense-account-widget-positive">
            {{ formatCurrency(totalIncome) }}
          </div>
        </div>
        <div class="income-expense-account-widget-detail-row">
          <div class="income-expense-account-widget-detail-label">
            <span class="income-expense-account-widget-label-text">COST OF GOODS SOLD</span>
            <span 
              class="income-expense-account-widget-info-icon" 
              @click="showInfoModal('Cost of Goods Sold', 'Cost of Goods Sold = Sum of all COGS accounts', 'Cost of Goods Sold (COGS) represents the direct costs attributable to the production of goods or services sold by a company.')"
              title="What is Cost of Goods Sold?"
            >
              <i class="bi bi-question-circle"></i>
            </span>
          </div>
          <div class="income-expense-account-widget-detail-value income-expense-account-widget-negative">
            {{ formatCurrency(totalCOGS) }}
          </div>
        </div>
        <div class="income-expense-account-widget-detail-row">
          <div class="income-expense-account-widget-detail-label">
            <span class="income-expense-account-widget-label-text">GROSS PROFIT</span>
            <span 
              class="income-expense-account-widget-info-icon" 
              @click="showInfoModal('Gross Profit', 'Gross Profit = Total Income - Cost of Goods Sold', 'Gross Profit represents the profit a company makes after deducting the costs directly associated with making and selling its products or services.')"
              title="What is Gross Profit?"
            >
              <i class="bi bi-question-circle"></i>
            </span>
          </div>
          <div class="income-expense-account-widget-detail-value income-expense-account-widget-positive">
            {{ formatCurrency(grossProfit) }}
          </div>
        </div>
        <div class="income-expense-account-widget-detail-row">
          <div class="income-expense-account-widget-detail-label">
            <span class="income-expense-account-widget-label-text">OPERATING EXPENSES</span>
            <span 
              class="income-expense-account-widget-info-icon" 
              @click="showInfoModal('Operating Expenses', 'Operating Expenses = Expense + Other Expense', 'Operating Expenses are the costs a company incurs through its normal business operations, excluding Cost of Goods Sold.')"
              title="What are Operating Expenses?"
            >
              <i class="bi bi-question-circle"></i>
            </span>
          </div>
          <div class="income-expense-account-widget-detail-value income-expense-account-widget-negative">
            {{ formatCurrency(operatingExpenses) }}
          </div>
        </div>
        <div class="income-expense-account-widget-detail-row">
          <div class="income-expense-account-widget-detail-label">
            <span class="income-expense-account-widget-label-text">OPERATING INCOME</span>
            <span 
              class="income-expense-account-widget-info-icon" 
              @click="showInfoModal('Operating Income', 'Operating Income = Gross Profit - Operating Expenses', 'Operating Income is the profit realized from a business\'s operations after subtracting operating expenses from gross profit.')"
              title="What is Operating Income?"
            >
              <i class="bi bi-question-circle"></i>
            </span>
          </div>
          <div class="income-expense-account-widget-detail-value income-expense-account-widget-positive">
            {{ formatCurrency(operatingIncome) }}
          </div>
        </div>
        <div class="income-expense-account-widget-detail-row">
          <div class="income-expense-account-widget-detail-label">
            <span class="income-expense-account-widget-label-text">NET INCOME</span>
            <span 
              class="income-expense-account-widget-info-icon" 
              @click="showInfoModal('Net Income', 'Net Income = Operating Income', 'Net Income is the total profit or loss after all expenses have been deducted from total revenue.')"
              title="What is Net Income?"
            >
              <i class="bi bi-question-circle"></i>
            </span>
            <span 
              class="income-expense-account-widget-chart-icon" 
              @click="showChartView"
              title="View chart"
            >
              <i class="bi bi-bar-chart"></i>
            </span>
          </div>
          <div class="income-expense-account-widget-detail-value" :class="netIncomeClass">
            {{ formatCurrency(netIncome) }}
          </div>
        </div>
      </div>
    </div>
    
    <!-- Info Modal -->
    <div v-if="showInfo" class="income-expense-account-widget-modal-overlay" @click.self="closeInfoModal">
      <div class="income-expense-account-widget-modal">
        <div class="income-expense-account-widget-modal-header">
          {{ infoTitle }}
          <span class="income-expense-account-widget-modal-close" @click="closeInfoModal">✖</span>
        </div>
        <div class="income-expense-account-widget-modal-body">
          <div class="income-expense-account-widget-modal-formula">{{ infoFormula }}</div>
          <div class="income-expense-account-widget-modal-description">{{ infoDescription }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './styles/IncomeExpenseAccountWidget.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';

export default {
  name: 'IncomeExpenseAccountWidget',
  props: {
    companyId: { type: [String, Number], required: true }
  },
  data() {
    return {
      incomeAccounts: [],
      cogsAccounts: [],
      operatingExpenseAccounts: [],
      loading: true,
      error: null,
      showInfo: false,
      infoTitle: '',
      infoFormula: '',
      infoDescription: ''
    };
  },
  computed: {
    totalIncome() {
      return this.incomeAccounts.reduce((sum, acc) => sum + Math.abs(acc.balance || 0), 0);
    },
    totalCOGS() {
      return this.cogsAccounts.reduce((sum, acc) => sum + Math.abs(acc.balance || 0), 0);
    },
    operatingExpenses() {
      return this.operatingExpenseAccounts.reduce((sum, acc) => sum + Math.abs(acc.balance || 0), 0);
    },
    grossProfit() {
      return this.totalIncome - this.totalCOGS;
    },
    operatingIncome() {
      return this.grossProfit - this.operatingExpenses;
    },
    netIncome() {
      return this.operatingIncome;
    },
    netIncomeClass() {
      return this.netIncome >= 0 ? 'income-expense-account-widget-positive' : 'income-expense-account-widget-negative';
    },
    totalExpenses() {
      return this.totalCOGS + this.operatingExpenses;
    },
    incomePercentage() {
      const total = this.totalIncome + this.totalExpenses;
      if (total === 0) return 50;
      return (this.totalIncome / total) * 100;
    },
    expensePercentage() {
      const total = this.totalIncome + this.totalExpenses;
      if (total === 0) return 50;
      return (this.totalExpenses / total) * 100;
    }
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        // Fetch all accounts with their balances
        const response = await ApiService.get(`/company-account/company/${this.companyId}`);
        const accountsData = Array.isArray(response.data) ? response.data : (response.data?.accounts || []);
        
        // Separate accounts by type
        const incomeTypes = ['Income', 'Other Income'];
        const cogsTypes = ['Cost of Goods Sold'];
        const operatingExpenseTypes = ['Expense', 'Other Expense'];
        
        this.incomeAccounts = accountsData
          .filter(acc => acc.active !== false && incomeTypes.includes(acc.accountType))
          .map(acc => ({
            id: acc.id,
            accountName: acc.accountName || acc.name,
            balance: parseFloat(acc.currentBalance || acc.balance || 0)
          }));
        
        this.cogsAccounts = accountsData
          .filter(acc => acc.active !== false && cogsTypes.includes(acc.accountType))
          .map(acc => ({
            id: acc.id,
            accountName: acc.accountName || acc.name,
            balance: parseFloat(acc.currentBalance || acc.balance || 0)
          }));
        
        this.operatingExpenseAccounts = accountsData
          .filter(acc => acc.active !== false && operatingExpenseTypes.includes(acc.accountType))
          .map(acc => ({
            id: acc.id,
            accountName: acc.accountName || acc.name,
            balance: parseFloat(acc.currentBalance || acc.balance || 0)
          }));
      } catch (error) {
        logger.error('Error fetching income/expense data:', error);
        this.error = 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    },
    showInfoModal(title, formula, description) {
      this.infoTitle = title;
      this.infoFormula = formula;
      this.infoDescription = description;
      this.showInfo = true;
    },
    closeInfoModal() {
      this.showInfo = false;
    },
    showChartView() {
      // Navigate to profit and loss report or show chart
      this.$router.push(`/company/${this.companyId}/reports/profit-and-loss`);
    }
  }
};
</script>

